import Immutable from 'immutable';
import animateScrollTo from 'animated-scroll-to';
import { get, getPathWithQueryString, pageContext, logMessage } from 'ctm-util-functions';
import { locationQuerySelector } from 'ctm-selectors';
import { resetJourneyPayloads } from '../prefill/utils';

/**
 * Set the current router location on state.
 * @param {Object} pageModel        - The model for the page.
 * @param {function} dispatch       - The dispatch function.
 */
const setLocationOnState = (pageModel, dispatch) => {
    const browserHistory = pageContext.getProps().browserHistory;
    if (pageContext.getProps().isClient) {
        // find page schema for current location
        const pageSchema = pageModel.getPageSchema(browserHistory.location.pathname);

        dispatch({
            type: 'SET_ROUTER_LOCATION',
            location: browserHistory.location,
            name: pageSchema.name,
            title: pageSchema.title
        });

        animateScrollTo(0);
    }
};

/**
 * Makes the browserHistory pop or replace a route.
 * @param {Object} pageModel - The model for the page.
 * @param {Immutable.Map} state - The application state.
 * @param {String} journeyFirstStepPath - The path of the first step in the journey.
 * @param {String} nextPagePath - The next page path.
 * @returns {Boolean} - true if the route has changed, false otherwise
 */
const updateBrowserHistory = (pageModel, state, journeyFirstStepPath, nextPagePath) => {
    const prevStatePageName = state.getIn(['page', 'name']);
    const journeyLastPage = pageModel.getPageJourneyLastPage();
    const preventTransition = pageModel.preventTransition(prevStatePageName, nextPagePath);
    const prevStatePagePath = pageModel.getPathForPageName(prevStatePageName);
    const isLoadJourney = locationQuerySelector(state).get('load_journey') === 'true';
    const browserHistory = pageContext.getProps().browserHistory;
    let isRouteChanged = true;

    // check if the browser history pops from the journey's last page, but allows load_journey
    // @todo: clean this up, it shouldn't rely on the order of the page to redirect to start, @see: EVT-1691
    if (prevStatePageName === journeyLastPage.name && !isLoadJourney) {
        browserHistory.replace(getPathWithQueryString(journeyFirstStepPath));
        browserHistory.go(getPathWithQueryString(journeyFirstStepPath));
    }

    // check if the browser history pops to a previous page.
    // if so `replace` with previousStatePagePath *NOT* nextStatePagePath
    if (preventTransition) {
        browserHistory.replace(getPathWithQueryString(prevStatePagePath));
        isRouteChanged = false;
    }

    return isRouteChanged;
};

/**
 * Check if the user should be redirected to a conditional step, if so, perform the redirect
 * @param {Boolean} isSSR - True if this is this being called during a server render, false for a client render
 * @param {Object} nextLocation - Location information passed from react-router for the next, default location,
 * @returns {Boolean} - true if a redirect has been performed
 */
export const redirectIfNeeded = (isSSR, nextLocation) => {
    if (isSSR) {
        // We can't redirect via browserHistory on the server. This gets run when the PageWrapper mounts, is it's SSR
        // any redirects have already been performed, so don't do anything is this is run on the server.
        return false;
    }
    const { store, models } = pageContext.getProps();
    const { pageSchemaModel, progressionModel } = models;

    if (!nextLocation || typeof nextLocation.pathname !== 'string') {
        return false;
    }

    const isFirstPage = pageSchemaModel.isFirstStepInJourney(nextLocation.pathname);

    // @todo: currently we're assuming that the first page isn't conditional, this may change based on values received from wordpress
    // if the first page was conditional, we'd need to account for this on the server rather than in here anyway
    const browserHistory = pageContext.getProps().browserHistory;
    if (!isFirstPage) {
        // check if there are progression rules for the next page, if the rules and state require a redirect to a
        // different next page, redirect to that page.
        let nextPagePath;
        try {
            nextPagePath = progressionModel.getNextPage(nextLocation.pathname, store.getState());
        } catch (e) {
            // swallow errors from progressionModel
            return false;
        }
        if (typeof nextPagePath === 'string' && nextPagePath && nextPagePath !== nextLocation.pathname) {
            _dispatchOnNextActions(pageSchemaModel, nextLocation, store);
            // eslint-disable-next-line no-console
            logMessage(`Redirecting (Router-level): ${nextPagePath}`);
            if (browserHistory.action === 'POP') {
                _goBackUntilRouteMatches(browserHistory, nextPagePath);
            } else {
                browserHistory.replace(getPathWithQueryString(nextPagePath));
            }
            return true;
        }
    }
    return false;
};

/**
 * This method solves an issue in Car where onNext actions are not dispatched when a progression schema rule skips a step.
 * This works for ONE instance of it, but not a second one, because the API call isn't waiting.
 * @see CTMCE-62
 * @param {Object} pageSchemaModel - Instance of the class "PageSchemaModel" initialized with "page_schema.json".
 * @param {Object} nextLocation - Location information passed from react-router,
 * see https://github.com/ReactTraining/react-router/blob/bd147e88c9a162f740fb7f9cd4a218343603e3ee/docs/Glossary.md#locationdescriptor
 * @param {Object} store - The application store
 * @private
 */
const _dispatchOnNextActions = (pageSchemaModel, nextLocation, store) => {
    const nextPath = nextLocation.pathname;
    const currentPage = store.getState().getIn(['page', 'name']);
    const isGoingBack = pageSchemaModel.isLaterPageInSchema(currentPage, nextPath);
    if (isGoingBack) {
        // Don't process any actions when going back
        return;
    }

    const nextPageSchema = pageSchemaModel.getPageSchema(nextLocation.pathname);
    if (nextPageSchema) {
        const apiSchema = get(nextPageSchema, 'api');
        if (apiSchema) {
            store.dispatch({
                type: 'VALIDATE_AND_PROGRESS',
                pageName: nextPageSchema.name,
                apiSchema,
                stateKey: 'vertical.quote' // @todo get this from somewhere, should be fine for now
            });
        }
    }
};

/**
 * This method solves an issue with the back button where redirecting can cause a duplicate path in the browserHistory causing the back button to not work as expected.
 * Instead of replacing the current path and risking creating a duplicate, this method will keep going back in the browser history until it finds the correct path.
 * @param {Object} browserHistory - The browser history, used to manipulate the browser history and check the current path.
 * @param {String} nextPagePath - The path of the next page to go to, which is being checked against the current path in the browser history.
 * @private
 */
const _goBackUntilRouteMatches = (browserHistory, nextPagePath) => {
    // Helper function to go back until the route matches
    let goBackTimeoutId = null;
    const goBackUntilMatch = () => {
        const currentPath = browserHistory.location.pathname;
        // Go back until the currentPath equals the expected path, or we hit the start of the journey (to prevent going back indefinitely)
        if (currentPath !== nextPagePath && !(currentPath === '/home-contents-insurance/journey/start' || currentPath === '/car-insurance/journey/start')) {
            browserHistory.go(-1);
            // Clear any existing timeout before setting a new one
            if (goBackTimeoutId) {
                clearTimeout(goBackTimeoutId);
            }
            goBackTimeoutId = setTimeout(goBackUntilMatch, 50);
        } else if (goBackTimeoutId) {
            // Clear timeout when match is found
            clearTimeout(goBackTimeoutId);
            goBackTimeoutId = null;
        }
    };
    goBackUntilMatch();
};

/**
 * This onEnter transition hook is called when a route is about to be entered.
 * It prevents loading directly a journey page (except start) with Server Side Rendering.
 * eg: trying to go straight to /contact without going via /start will cause the app to redirect to /start
 *
 * @param {Object} currentPageProps - The current props for the requested page
 * @returns {Object} - Object contains two booleans that indicates if the page is redirecting and if the page route has changed
 */
export const onRouteChange = (currentPageProps) => {
    const { models, store } = pageContext.getProps();
    const vertical = pageContext.getVertical();

    const state = store.getState();
    const pageSchemaModel = models.pageSchemaModel;
    const prevLocation = get(state, 'page.location', Immutable.Map()).toJS();
    const nextLocation = get(currentPageProps, 'location', {});
    const { path: journeyFirstStepPath } = pageSchemaModel.getFirstStepPage();
    const isPrefillPage = pageSchemaModel.isPrefillInJourney(nextLocation.pathname);
    const isVerifyPage = pageSchemaModel.isVerifyInJourney(nextLocation.pathname);
    const isQuotesExpiredPage = pageSchemaModel.isQuotesExpiredInJourney(nextLocation.pathname);

    // Perform redirect to a conditional page if required
    const isRedirecting = redirectIfNeeded(currentPageProps.isSSR, nextLocation);

    // if page path/route isn't changing, we don't have to do a lot of things e.g. onMount APIs
    let isRouteChanged = true;

    const browserHistory = pageContext.getProps().browserHistory;
    // Prevent the go forward and if the user pops the browser history.
    // The reason we want to stop the forward button is because the journey is dynamic and
    // if the user clicks back and changes the inputted data the journey could potentially change
    // meaning if we allow them to use the browsers forward button they could end up on the wrong page.
    if (browserHistory.action === 'POP' && prevLocation.pathname) {
        // Reset the journey if prefill_check is the first step
        if (nextLocation.pathname === journeyFirstStepPath) {
            const resetJourneyPayload = resetJourneyPayloads[vertical];
            if (resetJourneyPayload) {
                resetJourneyPayload(store.dispatch);
            }
        }
        // prevents issue where user goes back to journey to edit details and presses the back button
        if (nextLocation.pathname.split('/').splice(-1)[0] !== 'results') {
            isRouteChanged = updateBrowserHistory(pageSchemaModel, state, journeyFirstStepPath, nextLocation.pathname);
        }
        if (nextLocation.pathname.split('/').splice(-1)[0] !== 'results' && !isPrefillPage && !isVerifyPage && !isQuotesExpiredPage) {
            isRouteChanged = updateBrowserHistory(pageSchemaModel, state, journeyFirstStepPath, nextLocation.pathname);
        }
    }

    // This prevents dispatching the actions more than once for the same route.
    if (nextLocation.pathname !== prevLocation.pathname && !isRedirecting && isRouteChanged) {
        // This event will get fired during SSR as well as on client route progression.
        store.dispatch({ type: 'SET_VERTICAL_NAME', vertical });
        // This set the Router location in the state.
        setLocationOnState(pageSchemaModel, store.dispatch);
    }

    return { isRedirecting, isRouteChanged };
};
